// =============================================================================
// AGC: DROID FARM - Field Monitoring Hook
// AGC: React hook for fetching and managing field health, observations, and alerts
// AGC: Integrates with backend API and local/Firestore caching
// =============================================================================

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useAuth } from './useAuth'
import {
  fetchFieldHealth,
  fetchTrendAnalysis,
  fetchManagementZones,
  fetchNdviOverlayUrl,
  getFieldObservations,
  getStressEvents,
  updateStressEventStatus,
  generateAlertSummary,
  clearAllMonitoringCache,
} from '../services/fieldMonitoring'
import type {
  FieldHealthResult,
  FieldObservation,
  StressEvent,
  ManagementZone,
  TrendAnalysisResult,
  GrowthStage,
  AlertSummary,
  NdviOverlayConfig,
  MonitoringFetchOptions,
  StressEventStatus,
} from '../types'

// =============================================================================
// AGC: Hook Types
// =============================================================================

export interface UseFieldMonitoringProps {
  fieldId: string
  fieldPolygon?: [number, number][]
  bounds?: {
    northEast: { lat: number; lng: number }
    southWest: { lat: number; lng: number }
  }
  autoFetch?: boolean
  fetchInterval?: number // AGC: Auto-refresh interval in ms (0 = disabled)
}

export interface UseFieldMonitoringReturn {
  // AGC: Data
  health: FieldHealthResult | null
  observations: FieldObservation[]
  stressEvents: StressEvent[]
  zones: ManagementZone[] | null
  trend: TrendAnalysisResult | null
  growthStage: GrowthStage | null
  alertSummary: AlertSummary | null
  ndviOverlay: NdviOverlayConfig | null

  // AGC: State
  isLoading: boolean
  isRefreshing: boolean
  error: string | null
  lastUpdated: string | null

  // AGC: Actions
  refetch: (options?: MonitoringFetchOptions) => Promise<void>
  fetchHealth: (options?: MonitoringFetchOptions) => Promise<FieldHealthResult | null>
  fetchTrend: (options?: { startDate?: string; endDate?: string }) => Promise<TrendAnalysisResult | null>
  fetchZones: (numZones?: number, forceRefresh?: boolean) => Promise<ManagementZone[] | null>
  fetchNdviOverlay: (date?: string) => Promise<string | null>
  updateEventStatus: (eventId: string, status: StressEventStatus) => Promise<void>
  clearCache: () => void
}

// =============================================================================
// AGC: Hook Implementation
// =============================================================================

export function useFieldMonitoring({
  fieldId,
  fieldPolygon,
  bounds,
  autoFetch = true,
  fetchInterval = 0,
}: UseFieldMonitoringProps): UseFieldMonitoringReturn {
  const { getAuthToken, user } = useAuth()

  // AGC: State
  const [health, setHealth] = useState<FieldHealthResult | null>(null)
  const [observations, setObservations] = useState<FieldObservation[]>([])
  const [stressEvents, setStressEvents] = useState<StressEvent[]>([])
  const [zones, setZones] = useState<ManagementZone[] | null>(null)
  const [trend, setTrend] = useState<TrendAnalysisResult | null>(null)
  const [ndviOverlay, setNdviOverlay] = useState<NdviOverlayConfig | null>(null)

  const [isLoading, setIsLoading] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<string | null>(null)

  // ==========================================================================
  // AGC: Derived Data
  // ==========================================================================

  // AGC: Generate alert summary from health and events
  const alertSummary = useMemo(() => {
    return generateAlertSummary(health, stressEvents)
  }, [health, stressEvents])

  // AGC: Estimate growth stage from trend data
  const growthStage = useMemo((): GrowthStage | null => {
    if (!health?.health?.metrics?.meanNdvi) return null

    const currentNdvi = health.health.metrics.meanNdvi
    const trendDirection = trend?.trend?.direction || 'stable'

    // AGC: Simple growth stage estimation
    let stage: GrowthStage['stage']
    let description: string

    if (currentNdvi < 0.2) {
      stage = 'bare_soil'
      description = 'Bare soil or recently planted'
    } else if (currentNdvi < 0.35 && trendDirection === 'increasing') {
      stage = 'emergence'
      description = 'Early growth / emergence phase'
    } else if (currentNdvi >= 0.35 && currentNdvi < 0.5 && trendDirection === 'increasing') {
      stage = 'vegetative'
      description = 'Active vegetative growth'
    } else if (currentNdvi >= 0.5 && currentNdvi < 0.7 && trendDirection === 'increasing') {
      stage = 'development'
      description = 'Rapid development phase'
    } else if (currentNdvi >= 0.7 && trendDirection !== 'decreasing') {
      stage = 'peak'
      description = 'Peak vegetative growth / canopy closure'
    } else if (currentNdvi >= 0.5 && trendDirection === 'decreasing') {
      stage = 'maturation'
      description = 'Maturation / grain filling'
    } else if (currentNdvi >= 0.3 && currentNdvi < 0.5 && trendDirection === 'decreasing') {
      stage = 'senescence'
      description = 'Senescence / drying down'
    } else if (currentNdvi < 0.3 && trendDirection === 'decreasing') {
      stage = 'harvest_ready'
      description = 'Ready for harvest or post-harvest'
    } else {
      stage = 'unknown'
      description = 'Unable to determine growth stage'
    }

    return {
      stage,
      description,
      currentNdvi,
      trendDirection,
      cropType: 'generic',
      confidence: stage === 'unknown' ? 'low' : 'moderate',
      note: 'Estimation based on NDVI patterns. For accurate staging, combine with field observations.',
    }
  }, [health, trend])

  // ==========================================================================
  // AGC: Fetch Functions
  // ==========================================================================

  /**
   * AGC: Fetch field health data
   */
  const fetchHealth = useCallback(async (options: MonitoringFetchOptions = {}): Promise<FieldHealthResult | null> => {
    if (!bounds) {
      console.warn('AGC: useFieldMonitoring - bounds required for health fetch')
      return null
    }

    console.log('AGC: Fetching health for field:', fieldId, 'bounds:', bounds)

    try {
      const token = await getAuthToken()
      const result = await fetchFieldHealth(
        fieldId,
        bounds,
        fieldPolygon,
        token,
        {
          timeWindowDays: options.timeWindowDays || 30,
          forceRefresh: options.forceRefresh,
        }
      )

      console.log('AGC: Health result received:', result ? 'success' : 'null', result?.health?.score)

      if (result) {
        setHealth(result)
        setLastUpdated(new Date().toISOString())
      }

      return result
    } catch (err) {
      console.error('AGC: Error fetching health:', err)
      throw err
    }
  }, [fieldId, bounds, fieldPolygon, getAuthToken])

  /**
   * AGC: Fetch NDVI trend analysis
   */
  const fetchTrend = useCallback(async (options: { startDate?: string; endDate?: string } = {}): Promise<TrendAnalysisResult | null> => {
    if (!bounds) {
      console.warn('AGC: useFieldMonitoring - bounds required for trend fetch')
      return null
    }

    try {
      const token = await getAuthToken()
      const result = await fetchTrendAnalysis(fieldId, bounds, token, options)

      if (result) {
        setTrend(result)
      }

      return result
    } catch (err) {
      console.error('AGC: Error fetching trend:', err)
      throw err
    }
  }, [fieldId, bounds, getAuthToken])

  /**
   * AGC: Fetch management zones
   */
  const fetchZonesFunc = useCallback(async (numZones = 3, forceRefresh = false): Promise<ManagementZone[] | null> => {
    if (!bounds) {
      console.warn('AGC: useFieldMonitoring - bounds required for zones fetch')
      return null
    }

    try {
      const token = await getAuthToken()
      console.log('AGC: Fetching zones for field:', fieldId, 'forceRefresh:', forceRefresh)
      const result = await fetchManagementZones(fieldId, bounds, token, { numZones, forceRefresh })

      if (result?.zones) {
        console.log('AGC: Zones result:', result.zones.length, 'zones received')
        setZones(result.zones)
        return result.zones
      }

      console.log('AGC: No zones returned from API')
      return null
    } catch (err) {
      console.error('AGC: Error fetching zones:', err)
      throw err
    }
  }, [fieldId, bounds, getAuthToken])

  /**
   * AGC: Fetch NDVI overlay URL
   */
  const fetchNdviOverlayFunc = useCallback(async (date?: string): Promise<string | null> => {
    if (!bounds) {
      console.warn('AGC: useFieldMonitoring - bounds required for overlay fetch')
      return null
    }

    try {
      const url = await fetchNdviOverlayUrl(fieldId, bounds, { date })

      if (url) {
        setNdviOverlay({
          url,
          bounds: [
            [bounds.southWest.lat, bounds.southWest.lng],
            [bounds.northEast.lat, bounds.northEast.lng],
          ],
          opacity: 0.7,
          date: date || new Date().toISOString().split('T')[0],
        })
        return url
      }

      return null
    } catch (err) {
      console.error('AGC: Error fetching overlay:', err)
      throw err
    }
  }, [fieldId, bounds])

  /**
   * AGC: Update stress event status
   */
  const updateEventStatus = useCallback(async (eventId: string, status: StressEventStatus): Promise<void> => {
    try {
      await updateStressEventStatus(eventId, fieldId, status, user?.uid)
      
      // AGC: Refresh events list
      const events = await getStressEvents(fieldId, { forceRefresh: true })
      setStressEvents(events)
    } catch (err) {
      console.error('AGC: Error updating event status:', err)
      throw err
    }
  }, [fieldId, user])

  /**
   * AGC: Main refetch function - fetches all monitoring data
   */
  const refetch = useCallback(async (options: MonitoringFetchOptions = {}): Promise<void> => {
    if (!fieldId) {
      console.warn('AGC: useFieldMonitoring - fieldId required')
      return
    }

    const isInitialLoad = !health && !isLoading
    if (isInitialLoad) {
      setIsLoading(true)
    } else {
      setIsRefreshing(true)
    }

    setError(null)

    try {
      // AGC: Fetch all data in parallel
      const promises: Promise<unknown>[] = []

      // AGC: Always fetch observations and events
      promises.push(
        getFieldObservations(fieldId, { forceRefresh: options.forceRefresh })
          .then(obs => setObservations(obs))
          .catch(err => console.warn('AGC: Error fetching observations:', err))
      )

      promises.push(
        getStressEvents(fieldId, { forceRefresh: options.forceRefresh })
          .then(events => setStressEvents(events))
          .catch(err => console.warn('AGC: Error fetching events:', err))
      )

      // AGC: Fetch health if bounds available
      if (bounds) {
        promises.push(
          fetchHealth(options).catch(err => console.warn('AGC: Error fetching health:', err))
        )
      }

      // AGC: Optionally fetch trend
      if (options.includeTrend && bounds) {
        promises.push(
          fetchTrend().catch(err => console.warn('AGC: Error fetching trend:', err))
        )
      }

      // AGC: Optionally fetch zones
      if (options.includeZones && bounds) {
        promises.push(
          fetchZonesFunc().catch(err => console.warn('AGC: Error fetching zones:', err))
        )
      }

      await Promise.all(promises)
      setLastUpdated(new Date().toISOString())
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch monitoring data'
      setError(message)
      console.error('AGC: Error in refetch:', err)
    } finally {
      setIsLoading(false)
      setIsRefreshing(false)
    }
  }, [fieldId, bounds, health, isLoading, fetchHealth, fetchTrend, fetchZonesFunc])

  /**
   * AGC: Clear all cached data
   */
  const clearCache = useCallback(() => {
    clearAllMonitoringCache()
    setHealth(null)
    setObservations([])
    setStressEvents([])
    setZones(null)
    setTrend(null)
    setNdviOverlay(null)
    setLastUpdated(null)
  }, [])

  // ==========================================================================
  // AGC: Effects
  // ==========================================================================

  // AGC: Reset auto-fetch flag when fieldId changes (navigating between lots)
  const hasFetchedRef = useRef(false)
  const prevFieldIdRef = useRef(fieldId)
  if (prevFieldIdRef.current !== fieldId) {
    prevFieldIdRef.current = fieldId
    hasFetchedRef.current = false
  }

  // AGC: Auto-fetch on mount when bounds become available
  // AGC: Uses ref to prevent duplicate fetches and avoid stale-closure issues
  useEffect(() => {
    if (autoFetch && fieldId && bounds && !hasFetchedRef.current) {
      hasFetchedRef.current = true
      console.log('AGC: useFieldMonitoring - Auto-fetching with bounds:', bounds)
      refetch({ includeTrend: true, includeZones: false })
    }
  }, [autoFetch, fieldId, bounds, refetch])

  // AGC: Auto-refresh interval
  useEffect(() => {
    if (fetchInterval > 0 && fieldId) {
      const interval = setInterval(() => {
        refetch()
      }, fetchInterval)

      return () => clearInterval(interval)
    }
  }, [fetchInterval, fieldId, refetch])

  // ==========================================================================
  // AGC: Return
  // ==========================================================================

  return {
    // AGC: Data
    health,
    observations,
    stressEvents,
    zones,
    trend,
    growthStage,
    alertSummary,
    ndviOverlay,

    // AGC: State
    isLoading,
    isRefreshing,
    error,
    lastUpdated,

    // AGC: Actions
    refetch,
    fetchHealth,
    fetchTrend,
    fetchZones: fetchZonesFunc,
    fetchNdviOverlay: fetchNdviOverlayFunc,
    updateEventStatus,
    clearCache,
  }
}

export default useFieldMonitoring

